/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Stockton Jenkins <stockton.jenkins@deliciousai.com>
 */

import { AutoAwesome, DoubleArrow } from '@mui/icons-material'
import { Paper, Stack, Typography } from '@mui/material'
import React from 'react'
import Markdown from 'react-markdown'

import { PriceMismatchMetadataFragment } from '@/graphql'
import { formatCurrency } from '@/i18n/tolgee'
import useT from '@/i18n/useT'
import { ProductImage } from '@/pages/dashboards/Default/ProductImage'
import { MarkdownHelpers } from '@/utils/markdown.helpers'

type PriceMismatchInsightProps = {
  metadata: PriceMismatchMetadataFragment
}

const PriceMismatchInsight: React.FC<PriceMismatchInsightProps> = ({
  metadata,
}) => {
  const { t } = useT()
  return (
    <Stack spacing={4}>
      <Stack direction={'row'} justifyContent={'space-between'} spacing={2}>
        <Stack direction={'row'} alignItems={'center'} spacing={2}>
          <AutoAwesome />
          <Typography variant="h5">
            {t('aiInsightsTable.priceMismatch.identified')}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        pl={8}
        direction={'row'}
        alignItems={'center'}
        spacing={4}
        height={200}
      >
        <Stack spacing={2} height={'100%'}>
          <Paper
            variant={'outlined'}
            sx={{ bgcolor: 'white', flex: 1, alignContent: 'center' }}
          >
            <ProductImage
              upc={metadata.upc}
              width="100px"
              height="auto"
              maxHeight={'150px'}
              minHeight={'100px'}
              objectFit={'contain'}
              skeleton
            />
          </Paper>
          <Stack direction={'row'} justifyContent={'space-between'}>
            <Typography
              variant={'subtitle2'}
              fontWeight={'bold'}
              color="error.light"
              textAlign={'center'}
            >
              {formatCurrency(metadata.detectedUnitPrice, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
            <DoubleArrow />
            <Typography
              variant={'subtitle2'}
              fontWeight={'bold'}
              color="success.light"
              textAlign={'center'}
            >
              {formatCurrency(metadata.expectedUnitPrice, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Markdown>{MarkdownHelpers.format(metadata.message)}</Markdown>
    </Stack>
  )
}

export default PriceMismatchInsight
